// src/screens/catalogUpload/components/form/hooks/useForm.ts
import { SetStateAction, useCallback, useEffect, useState } from 'react'
import { CatUploadInterface } from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { CatUploadStatus } from '../../../../../../common/types/enums'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../../store/slices/currentClusterSlice'

interface CategoryMeta {
  name: string
  cleanBefore?: boolean
}

interface ParsedJsonData {
  categories: Array<{
    name: string
    originalLink: string
    description?: string
    categoryAttributes?: any[]
  }>
  items: Array<{
    categoryName: string
    originalLink: string
    name: string
    description?: string
    imageUrls?: string[]
    itemAttributes?: any[]
  }>
}

export const useForm = (
  data: CatUploadInterface,
  setData: React.Dispatch<SetStateAction<CatUploadInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: CatUploadInterface,
  initialData?: CatUploadInterface,
  modalActive?: boolean,
) => {
  const [salesApi] = useState(getSalesApi())
  const [file, setFile] = useState<File | null>(null)
  const [jsonData, setJsonData] = useState<ParsedJsonData | null>(null)
  const [categoriesMeta, setCategoriesMeta] = useState<CategoryMeta[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const currentCluster = useSelector(selectCurrentCluster)

  // Читаем JSON из файла
  useEffect(() => {
    if (!file) {
      setJsonData(null)
      setCategoriesMeta([])
      setSelectedCategories([])
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string)
        setJsonData(parsed)

        const meta =
          parsed.categories?.map((cat: any) => ({
            name: cat.name,
            cleanBefore: false,
          })) || []
        setCategoriesMeta(meta)
        setSelectedCategories(meta.map((cat: CategoryMeta) => cat.name))
      } catch (error) {
        console.error('Failed to parse JSON', error)
      }
    }
    reader.readAsText(file)
  }, [file])

  const toggleCategory = (name: string) => {
    setSelectedCategories((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name],
    )
  }

  const formAction = useCallback(async () => {
    if (!file || selectedCategories.length === 0) return

    try {
      const text = await file.text()
      const jsonData = JSON.parse(text)

      // Преобразуем товары: image → imageUrls (массив)
      const items = jsonData.items.map((item: any) => ({
        ...item,
        imageUrls: item.image ? [item.image] : [],
        image: undefined,
        productId: undefined,
        deleted: undefined,
      }))

      // Категории: imageUrl → imageUrls (массив)
      const categories = jsonData.categories.map((cat: any) => ({
        ...cat,
        imageUrls: cat.imageUrl ? [cat.imageUrl] : [],
        imageUrl: undefined,
      }))

      const payload = {
        clusterId: currentCluster?.id || Number(process.env.REACT_APP_CLUSTER_ID) || 0,
        categories,
        items,
        meta: {
          categories: selectedCategories.map((name) => ({
            name,
            cleanBefore: categoriesMeta.find((c) => c.name === name)?.cleanBefore || false,
          })),
        },
      }

      await salesApi.importCatalog(payload)
      await getList()
      setModalActive(false)
      setFile(null)
      setJsonData(null)
    } catch (error) {
      console.error('Import failed', error)
    }
  }, [file, selectedCategories, categoriesMeta, salesApi, getList, setModalActive, currentCluster])

  return {
    file,
    setFile,
    formAction,
    jsonData,
    categoriesMeta,
    setCategoriesMeta,
    selectedCategories,
    toggleCategory,
  }
}
